import { FormikContext, useFormik } from 'formik'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { ApiError } from '../../../state/api'
import * as api from '../../../state/api'
import useDialog from '../../../utilities/useDialog'
import Button from '../../basics/Button/Button'
import FormButtons from '../../basics/FormButtons/FormButtons'
import { TextInputFormik } from '../../basics/TextInput/TextInput'

import styles from './SetPasswordForm.module.scss'

interface SetPasswordFormProps {
  onSuccess?: () => void
  onAbort?: () => void

  emailOrUsername: string
  token?: string
}

export default React.memo(function SetPasswordForm({
  onSuccess,
  onAbort,
  emailOrUsername,
  token,
}: SetPasswordFormProps) {
  const { t } = useTranslation()
  const showDialog = useDialog()

  const formik = useFormik({
    initialValues: { currentPassword: '', password: '', passwordRepeat: '' },
    validate: ({ password, passwordRepeat }) => {
      if (password !== passwordRepeat) {
        return { passwordRepeat: t('login password.passwords do not match') }
      }
    },
    onSubmit: async (values, { setStatus, resetForm }) => {
      setStatus()

      try {
        const result = await api.post('password-reset', {
          password: values.password,
          token,
          email: emailOrUsername,
          current_password: values.currentPassword,
        })

        if (result.status === 'fail') {
          setStatus(result.message)
          return
        }

        resetForm()

        await showDialog((close) => (
          <div className={styles.passwordResetSuccessMessage}>
            <p>{t('login password.Your password was reset')}</p>
            <Button onClick={close}>{t('generic cta.ok')}</Button>
          </div>
        ))

        onSuccess?.()
      } catch (err) {
        if (err instanceof ApiError && err.code === 403) {
          setStatus(t('errors.password-invalid'))
        } else {
          setStatus(String(err))
        }
      }
    },
  })

  return (
    <FormikContext.Provider value={formik}>
      <form noValidate onSubmit={formik.handleSubmit}>
        <h1>{t('login password.set password')}</h1>
        {!token && (
          <TextInputFormik
            label={t('login password.current password')}
            type="password"
            name="currentPassword"
            autoComplete="password"
            autoFocus
            required
            disabled={formik.isSubmitting}
          />
        )}
        <TextInputFormik
          label={t('login password.new password')}
          type="password"
          name="password"
          autoComplete="password"
          autoFocus
          required
          minLength={8}
          disabled={formik.isSubmitting}
        />
        <TextInputFormik
          label={t('login password.new password repeat')}
          type="password"
          name="passwordRepeat"
          autoComplete="passwordRepeat"
          required
          minLength={8}
          disabled={formik.isSubmitting}
        />
        {formik.status && <p className={styles.errorMessage}>{formik.status}</p>}
        <FormButtons
          submitLabel={t('login password.cta set password')}
          onClose={onAbort}
          isSubmitting={formik.isSubmitting}
        />
      </form>
    </FormikContext.Provider>
  )
})
